import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageBody, PageHead } from '../components/Page';
import { Badge, type Tone } from '../components/Pill';
import { StatusBanner } from '../components/StatusBanner';
import { intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import type { SupportedLocale } from '../i18n';
import {
  ANY_CATEGORY,
  changedFields,
  filterByCategory,
  formFrom,
  snippetOrigin,
  unknownPlaceholders,
  validateSnippetForm,
  type SnippetForm,
  type SnippetOrigin,
} from '../lib/canned-responses';
import {
  CANNED_RESPONSE_CATEGORIES,
  archiveCannedResponse,
  createCannedResponse,
  isCannedResponseConflict,
  listCannedResponses,
  restoreCannedResponse,
  updateCannedResponse,
  type CannedResponse,
  type CannedResponseWriteResult,
} from '../lib/ops-canned-responses-api';
import { opsErrorMessage } from '../lib/ops-api';
import { isReasonAcceptable } from '../lib/shop-file';
import { supportEnumLabel } from '../lib/support-enum-labels';
import { useDebouncedValue } from '../lib/useDebouncedValue';

/** A date-time as the reader's locale writes it. */
function when(value: string, locale: SupportedLocale): string {
  return new Date(value).toLocaleString(intlLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Badge tone and copy per origin. */
const ORIGIN: Record<
  SnippetOrigin,
  { tone: Tone; label: string; hint: string }
> = {
  shipped: {
    tone: 'teal',
    label: 'cannedResponses.origin.shipped',
    hint: 'cannedResponses.origin.shippedHint',
  },
  editedDefault: {
    tone: 'amber',
    label: 'cannedResponses.origin.editedDefault',
    hint: 'cannedResponses.origin.editedDefaultHint',
  },
  custom: {
    tone: 'sky',
    label: 'cannedResponses.origin.custom',
    hint: 'cannedResponses.origin.customHint',
  },
};

/** Catalogue key per editor field, for the confirm step's "changed" line. */
const FIELD_LABEL = {
  title: 'cannedResponses.field.title',
  category: 'cannedResponses.field.category',
  bodyDe: 'cannedResponses.field.bodyDe',
  bodyEn: 'cannedResponses.field.bodyEn',
} as const;

/** Placeholder names with their help text. Mirrors what the list endpoint returns. */
const PLACEHOLDER_HELP: Record<string, string> = {
  shopName: 'cannedResponses.editor.placeholder.shopName',
  ticketReference: 'cannedResponses.editor.placeholder.ticketReference',
  agentName: 'cannedResponses.editor.placeholder.agentName',
};

/** What the editor is working on. */
type Editing =
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly base: CannedResponse };

/** An archive or restore waiting for its confirm step. */
interface StateChange {
  readonly action: 'archive' | 'restore';
  readonly snippet: CannedResponse;
}

/**
 * Canned responses: the catalogue of pre-approved answers (work order SUPPORT-TOOLS item 5, D12).
 *
 * Every operator may create, edit, archive and restore every snippet — there is no role model
 * (D11) — so each write asks for a reason, passes a confirm step and reports the audit row it wrote.
 * Nothing is ever deleted.
 *
 * Two operators editing one snippet cannot overwrite each other: the save names the version it was
 * made on, and a 409 reloads the list, shows the other operator's version next to the form and
 * keeps what this operator typed.
 */
export function CannedResponsesPage() {
  const { t, tCount, locale } = useLocale();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const [editing, setEditing] = useState<Editing | null>(null);
  const [form, setForm] = useState<SnippetForm>(formFrom());
  const [reason, setReason] = useState('');
  const [confirmingSave, setConfirmingSave] = useState(false);
  /** The version another operator saved while this one was editing — shown beside the form. */
  const [theirVersion, setTheirVersion] = useState<CannedResponse | null>(null);

  const [stateChange, setStateChange] = useState<StateChange | null>(null);
  const [stateReason, setStateReason] = useState('');

  const [result, setResult] = useState<CannedResponseWriteResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Category is filtered EXACTLY in the browser (see `filterByCategory`); only text and archive
  // state go to the API.
  const listQuery = useQuery({
    queryKey: ['canned-responses', 'manage', includeArchived, debouncedSearch],
    queryFn: () => listCannedResponses({ includeArchived, q: debouncedSearch }),
  });
  const allowed = listQuery.data?.placeholders ?? [];
  const snippets = filterByCategory(listQuery.data?.data ?? [], categoryFilter);

  const base = editing?.mode === 'edit' ? formFrom(editing.base) : formFrom();
  const changes = changedFields(base, form);
  const formErrors = validateSnippetForm(form, allowed);
  const unknown = [
    ...new Set([
      ...unknownPlaceholders(form.bodyDe, allowed),
      ...unknownPlaceholders(form.bodyEn, allowed),
    ]),
  ];
  const canSave =
    changes.length > 0 && formErrors.length === 0 && isReasonAcceptable(reason);

  const save = useMutation({
    mutationFn: () =>
      editing?.mode === 'edit'
        ? updateCannedResponse(
            editing.base.id,
            form,
            changes,
            editing.base.updatedAt,
            reason,
          )
        : createCannedResponse(form, reason),
    onSuccess: (written) => {
      setResult(written);
      closeEditor();
      void listQuery.refetch();
    },
    onError: async (error) => {
      setConfirmingSave(false);
      if (editing?.mode !== 'edit' || !isCannedResponseConflict(error)) return;
      // Someone saved in between. Take their version as the new base — so the next save is
      // checked against what is on the server now — but keep this operator's text in the form.
      const reloaded = await listQuery.refetch();
      const latest = reloaded.data?.data.find(
        (snippet) => snippet.id === editing.base.id,
      );
      if (latest) {
        setTheirVersion(latest);
        setEditing({ mode: 'edit', base: latest });
      } else {
        // No longer in the list as filtered (archived meanwhile, or no longer matching the
        // search): nothing to show beside the form, so say what happened and let them reopen it.
        closeEditor();
        setNotice(t('cannedResponses.stateConflict'));
      }
    },
  });

  const startEditing = (next: Editing) => {
    save.reset();
    setEditing(next);
    setForm(formFrom(next.mode === 'edit' ? next.base : null));
    setReason('');
    setTheirVersion(null);
    setResult(null);
    setNotice(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setTheirVersion(null);
    setConfirmingSave(false);
  };

  const changeState = useMutation({
    mutationFn: (change: StateChange) =>
      change.action === 'archive'
        ? archiveCannedResponse(change.snippet.id, stateReason)
        : restoreCannedResponse(change.snippet.id, stateReason),
    onSuccess: (written) => {
      setResult(written);
      setStateChange(null);
      setStateReason('');
      void listQuery.refetch();
    },
    onError: (error) => {
      setStateChange(null);
      setStateReason('');
      if (isCannedResponseConflict(error)) {
        setNotice(t('cannedResponses.stateConflict'));
        void listQuery.refetch();
      } else {
        setNotice(opsErrorMessage(error, t('cannedResponses.saveFailed')));
      }
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (canSave) setConfirmingSave(true);
  };

  const toggleExpanded = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <PageHead
        eyebrow={t('cannedResponses.eyebrow')}
        title={t('cannedResponses.title')}
        lead={t('cannedResponses.lead')}
        actions={
          <button
            type="button"
            onClick={() => startEditing({ mode: 'create' })}
            className="btn-primary btn-sm"
          >
            {t('cannedResponses.new')}
          </button>
        }
      />
      <PageBody>
        {result ? (
          <StatusBanner tone={result.auditLogId ? 'success' : 'warning'}>
            {result.auditLogId
              ? t('cannedResponses.saved', { id: result.auditLogId })
              : t('cannedResponses.savedNoAudit')}
          </StatusBanner>
        ) : null}
        {notice ? <StatusBanner tone="warning">{notice}</StatusBanner> : null}

        {editing ? (
          <form onSubmit={onSubmit} className="card space-y-4">
            <h2 className="text-base font-semibold text-gray-900">
              {editing.mode === 'edit'
                ? t('cannedResponses.editor.editTitle', {
                    title: editing.base.title,
                  })
                : t('cannedResponses.editor.createTitle')}
            </h2>

            {theirVersion ? (
              <StatusBanner tone="warning">
                <p>
                  {t('cannedResponses.editor.conflict', {
                    who: theirVersion.updatedBy,
                    time: when(theirVersion.updatedAt, locale),
                  })}
                </p>
                <p className="mt-2 font-medium">
                  {t('cannedResponses.editor.theirVersion')}
                </p>
                <div className="mt-1 grid gap-2 md:grid-cols-2">
                  <p className="whitespace-pre-wrap rounded-md bg-white p-2 text-xs text-gray-800">
                    {theirVersion.bodyDe}
                  </p>
                  <p className="whitespace-pre-wrap rounded-md bg-white p-2 text-xs text-gray-800">
                    {theirVersion.bodyEn}
                  </p>
                </div>
              </StatusBanner>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="cannedTitle" className="field-label">
                  {t('cannedResponses.editor.titleLabel')}
                </label>
                <input
                  id="cannedTitle"
                  value={form.title}
                  maxLength={120}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="field-input"
                />
              </div>
              <div>
                <label htmlFor="cannedCategory" className="field-label">
                  {t('cannedResponses.editor.categoryLabel')}
                </label>
                <select
                  id="cannedCategory"
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  className="field-input"
                >
                  <option value="">{t('cannedResponses.anyCategory')}</option>
                  {CANNED_RESPONSE_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {supportEnumLabel(t, 'category', category)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="cannedBodyDe" className="field-label">
                  {t('cannedResponses.editor.bodyDeLabel')}
                </label>
                <textarea
                  id="cannedBodyDe"
                  value={form.bodyDe}
                  rows={12}
                  onChange={(e) => setForm({ ...form, bodyDe: e.target.value })}
                  className="field-input"
                  lang="de"
                  spellCheck
                />
              </div>
              <div>
                <label htmlFor="cannedBodyEn" className="field-label">
                  {t('cannedResponses.editor.bodyEnLabel')}
                </label>
                <textarea
                  id="cannedBodyEn"
                  value={form.bodyEn}
                  rows={12}
                  onChange={(e) => setForm({ ...form, bodyEn: e.target.value })}
                  className="field-input"
                  lang="en"
                  spellCheck
                />
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <p className="font-medium text-gray-900">
                {t('cannedResponses.editor.placeholdersTitle')}
              </p>
              <p className="text-gray-600">
                {t('cannedResponses.editor.placeholdersIntro')}
              </p>
              <ul className="mt-2 space-y-1">
                {allowed.map((name) => (
                  <li key={name}>
                    {/* Built here, not in the catalogue: the i18n layer would fill the braces. */}
                    <code className="font-mono text-gray-900">{`{{${name}}}`}</code>{' '}
                    <span className="text-gray-600">
                      {PLACEHOLDER_HELP[name] ? t(PLACEHOLDER_HELP[name]) : ''}
                    </span>
                  </li>
                ))}
              </ul>
              {unknown.length > 0 ? (
                <p className="field-error mt-2">
                  {t('cannedResponses.editor.unknownPlaceholders', {
                    names: unknown.map((name) => `{{${name}}}`).join(', '),
                  })}
                </p>
              ) : null}
            </div>

            <div>
              <label htmlFor="cannedReason" className="field-label">
                {t('cannedResponses.editor.reasonLabel')}
              </label>
              <textarea
                id="cannedReason"
                value={reason}
                rows={2}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('cannedResponses.editor.reasonPlaceholder')}
                className="field-input"
              />
              <div className="flex justify-between items-center gap-4">
                <p className="field-hint">
                  {t('cannedResponses.editor.reasonHint')}
                </p>
                <p className="field-hint tabular-nums shrink-0">
                  {reason.length}/500
                </p>
              </div>
            </div>

            {formErrors
              .filter(
                (key) => key !== 'cannedResponses.errors.unknownPlaceholder',
              )
              .map((key) => (
                <p key={key} className="field-error">
                  {t(key)}
                </p>
              ))}
            {save.isError && !theirVersion ? (
              <StatusBanner tone="error">
                {opsErrorMessage(save.error, t('cannedResponses.saveFailed'))}
              </StatusBanner>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="submit"
                disabled={!canSave || save.isPending}
                className="btn-primary btn-sm"
              >
                {save.isPending
                  ? t('cannedResponses.editor.saving')
                  : t('cannedResponses.editor.save')}
              </button>
              <button
                type="button"
                onClick={closeEditor}
                className="btn-neutral btn-sm"
              >
                {t('common.cancel')}
              </button>
              {changes.length === 0 ? (
                <p className="field-hint">
                  {t('cannedResponses.editor.nothingChanged')}
                </p>
              ) : null}
            </div>
          </form>
        ) : null}

        <div className="card space-y-3">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_auto] md:items-end">
            <div>
              <label htmlFor="cannedSearch" className="field-label">
                {t('cannedResponses.filters.search')}
              </label>
              <input
                id="cannedSearch"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('cannedResponses.filters.searchPlaceholder')}
                className="field-input"
              />
            </div>
            <div>
              <label htmlFor="cannedCategoryFilter" className="field-label">
                {t('cannedResponses.filters.category')}
              </label>
              <select
                id="cannedCategoryFilter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="field-input"
              >
                <option value="">
                  {t('cannedResponses.filters.allCategories')}
                </option>
                <option value={ANY_CATEGORY}>
                  {t('cannedResponses.filters.anyTicket')}
                </option>
                {CANNED_RESPONSE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {supportEnumLabel(t, 'category', category)}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              {t('cannedResponses.filters.includeArchived')}
            </label>
          </div>
          {listQuery.data ? (
            <p className="field-hint">
              {tCount('cannedResponses.count', snippets.length)}
            </p>
          ) : null}
        </div>

        {listQuery.isError ? (
          <StatusBanner tone="error">
            {t('cannedResponses.loadError')}
          </StatusBanner>
        ) : listQuery.isLoading ? (
          <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
        ) : snippets.length === 0 ? (
          <StatusBanner tone="info">{t('cannedResponses.empty')}</StatusBanner>
        ) : (
          <ul className="space-y-3">
            {snippets.map((snippet) => {
              const origin = ORIGIN[snippetOrigin(snippet)];
              const open = expanded.has(snippet.id);
              return (
                <li key={snippet.id} className="surface p-4 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <p className="font-medium text-gray-900 break-words">
                        {snippet.title}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={origin.tone} title={t(origin.hint)}>
                          {t(origin.label)}
                        </Badge>
                        <Badge tone="gray">
                          {snippet.category
                            ? supportEnumLabel(t, 'category', snippet.category)
                            : t('cannedResponses.anyCategory')}
                        </Badge>
                        {snippet.archivedAt ? (
                          <Badge tone="red">
                            {t('cannedResponses.archivedBadge')}
                          </Badge>
                        ) : null}
                        {snippet.key ? (
                          <code className="text-xs text-gray-500 font-mono">
                            {snippet.key}
                          </code>
                        ) : null}
                      </div>
                      <p className="text-xs text-gray-500">
                        {t('cannedResponses.updated', {
                          who: snippet.updatedBy,
                          time: when(snippet.updatedAt, locale),
                        })}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 sm:shrink-0">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(snippet.id)}
                        aria-expanded={open}
                        className="btn-neutral btn-sm"
                      >
                        {t(
                          open
                            ? 'cannedResponses.hideText'
                            : 'cannedResponses.showText',
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          startEditing({ mode: 'edit', base: snippet })
                        }
                        className="btn-secondary btn-sm"
                      >
                        {t('cannedResponses.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setStateReason('');
                          setStateChange({
                            action: snippet.archivedAt ? 'restore' : 'archive',
                            snippet,
                          });
                        }}
                        className="btn-neutral btn-sm"
                      >
                        {t(
                          snippet.archivedAt
                            ? 'cannedResponses.restore'
                            : 'cannedResponses.archive',
                        )}
                      </button>
                    </div>
                  </div>
                  {open ? (
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium text-gray-500">
                          {t('cannedResponses.german')}
                        </p>
                        <p
                          lang="de"
                          className="whitespace-pre-wrap text-sm text-gray-800"
                        >
                          {snippet.bodyDe}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">
                          {t('cannedResponses.english')}
                        </p>
                        <p
                          lang="en"
                          className="whitespace-pre-wrap text-sm text-gray-800"
                        >
                          {snippet.bodyEn}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </PageBody>

      <ConfirmDialog
        open={confirmingSave}
        title={t(
          editing?.mode === 'edit'
            ? 'cannedResponses.confirm.editTitle'
            : 'cannedResponses.confirm.createTitle',
        )}
        confirmLabel={t('cannedResponses.confirm.save')}
        confirmDisabled={save.isPending}
        onConfirm={() => save.mutate()}
        onCancel={() => setConfirmingSave(false)}
      >
        <p className="font-medium text-gray-900">{form.title.trim()}</p>
        <p className="mt-2">
          {t('cannedResponses.confirm.fields', {
            fields: changes.map((field) => t(FIELD_LABEL[field])).join(', '),
          })}
        </p>
        <p className="mt-3">{t('cannedResponses.confirm.footer')}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={stateChange !== null}
        title={
          stateChange
            ? t(
                stateChange.action === 'archive'
                  ? 'cannedResponses.confirm.archiveTitle'
                  : 'cannedResponses.confirm.restoreTitle',
                { title: stateChange.snippet.title },
              )
            : ''
        }
        confirmLabel={t(
          stateChange?.action === 'restore'
            ? 'cannedResponses.confirm.restoreConfirm'
            : 'cannedResponses.confirm.archiveConfirm',
        )}
        confirmDisabled={
          !isReasonAcceptable(stateReason) || changeState.isPending
        }
        onConfirm={() => {
          if (stateChange) changeState.mutate(stateChange);
        }}
        onCancel={() => setStateChange(null)}
      >
        <p>
          {t(
            stateChange?.action === 'restore'
              ? 'cannedResponses.confirm.restoreBody'
              : 'cannedResponses.confirm.archiveBody',
          )}
        </p>
        <label htmlFor="cannedStateReason" className="field-label mt-4">
          {t('cannedResponses.editor.reasonLabel')}
        </label>
        <textarea
          id="cannedStateReason"
          value={stateReason}
          rows={2}
          maxLength={500}
          onChange={(e) => setStateReason(e.target.value)}
          className="field-input"
        />
        <p className="field-hint">{t('cannedResponses.editor.reasonHint')}</p>
      </ConfirmDialog>
    </>
  );
}
